import React, { ReactElement, memo, useState } from 'react';
import styled from '@emotion/styled';
import TopNav from '../../blocks/TopNav';
import Button from '../../../atoms/buttons/Button';
import Container from '../../blocks/Container';
import { Content, Aside, PrimaryContent } from '../../blocks/FlexLayout';
import ResultList from '../../blocks/ResultList';
import { StarIcon, EyeIcon, StarOutlineIcon, GridView, ListView } from '../../../atoms/icons/favoriteIcons';
import { getLocalItem, setLocalItem } from '../../../common';
import Loading from '../../../atoms/Loading';
import FavoritedList from './FavoritedList';
import { useLocation } from 'react-router-dom';
import { mixpanelTrackInterestsDisplayModeChanged } from '../../../utils/mixpanel/interestsDisplayModeChanged';
import { useDispatch, useSelector } from 'react-redux';
import { markAsReadInterested, refreshFavs, refreshInterested, refreshInterestedV2 } from '../../../actions/favActions';
import { RootState } from '../../../store';
import useMediaBreakpoints from '../../../hooks/useMediaBreakpoints';
import { INoResults } from './types/Interests';
import { isDarkMode } from '../../../utils/helpers';
import { SliderSourceLSKey } from '../../../config/constants';
import { loadMoreButtonStyle } from '../../common/CommonStyle';
import breakpoints from 'theme/material/breakpoints';
import { FONT_FAMILIES } from 'theme/material/constants';

const ButtonContainer = styled('div')({
  margin: '16px auto',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
});

const SubHeader = styled('div')(() => ({
  color: isDarkMode() ? '#AFB0B1' : '#212121',
  borderBottom: '1px solid rgba(88, 88, 88, 1)',
  paddingBottom: 10,
  flex: '1 0 100%',
  width: '100%',
  fontFamily: FONT_FAMILIES.secondary,
  fontSize: 24,
  lineHeight: '18px',
  letterSpacing: '-0.3px',
  [breakpoints.down(639.05)]: {
    paddingLeft: 0,
    paddingRight: 0,
    border: 'none',
  },
}));

const FavoritedListWrapper = styled('div')<{ isListView: boolean }>(
  {
    display: 'flex',
    flexFlow: 'row wrap',
    flex: '1 0 100%',
  },
  ({ isListView }) =>
    isListView && {
      padding: '0px 40px',
      marginLeft: 0,
      ['> * + *']: {
        marginLeft: 0,
      },
      [breakpoints.down(639.05)]: {
        marginLeft: '0vw',
        width: '100%',
        overflow: 'hidden',
      },
    }
);

const FavoritesList = styled(ResultList)({
  padding: '26px 40px',
  display: 'flex',
  flexFlow: 'wrap',
  borderRadius: '10px !important',
  boxShadow: 'none !important',
  [breakpoints.down(768.05)]: {
    padding: '16px 20px',
    borderRadius: '0 !important',
  },
  [breakpoints.down(639.05)]: {
    padding: 0,
    border: 'none',
  },
  justifyContent: 'center',
});

const ViewActions = styled('a')({
  float: 'right',
  cursor: 'pointer',
  height: 22,
  width: 22,
  [breakpoints.down(639.05)]: {
    marginRight: 15,
  },
});

const SubHeaderText = styled('span')(() => ({
  color: isDarkMode() ? '#AFB0B1' : '#212121',
  [breakpoints.down(639.05)]: {
    display: 'none',
  },
}));

interface InterestsProps {
  page: string;
}

const Interests = ({ page }: InterestsProps): ReactElement | null => {
  const { isMobile } = useMediaBreakpoints();
  const location = useLocation();

  const dispatch = useDispatch();
  const userProfile = useSelector((state: RootState) => state.profile);
  const fav = useSelector((state: RootState) => state.fav);
  const meta = useSelector((state: RootState) => state.meta);
  const { isFetching } = fav;
  const favList = fav[page];
  const currentPage = meta[`fav_${page}_current_page`];
  const totalPage = meta[`fav_${page}_total_pages`];
  const moreExists = currentPage < totalPage;

  const [viewType, setViewType] = useState(getLocalItem('favorites-view') || 'grid');
  const [subRoutes] = useState([
    {
      page: `Viewed Me`,
      icon: {
        default: <EyeIcon width={isMobile ? 14 : 18} isActive={false} />,
        active: <EyeIcon fill="#E21D4A" width={isMobile ? 14 : 18} isActive={true} />,
      },
      url: '/interests',
      active: /.*interests$/,
      noResultsTitle: `It takes two to tango!`,
      noResultsMsg: `The more you interact with other members, the more interest you'll get!`,
      noResultsImg: 'favorite',
    },
    {
      page: 'Favorited Me',
      icon: {
        default: <StarIcon width={isMobile ? 14 : 18} isActive={false} />,
        active: <StarIcon fill="#E21D4A" width={isMobile ? 14 : 18} isActive={true} />,
      },
      url: '/interests/favorited-me',
      active: /.*favorited-me.*/,
      noResultsTitle: 'It takes two to tango!',
      noResultsMsg: "The more you interact with other members, the more favorites you'll get!",
      noResultsImg: 'favorite',
    },
    {
      page: 'My Favorites',
      icon: {
        default: <StarOutlineIcon width={isMobile ? 14 : 18} isActive={false} />,
        active: <StarOutlineIcon fill="#E21D4A" width={isMobile ? 14 : 18} isActive={true} />,
      },
      url: '/interests/my-favorites',
      active: /.*my-favorites.*/,
      noResultsTitle: `Haven't found any favorites?`,
      noResultsMsg: `Use the star icon to add to your favorites list, or send them an offer! The more you do, the more you’ll get back.`,
      noResultsImg: 'favorite',
    },
  ]);

  if (!userProfile.profile) return null;

  const noResults: INoResults | any = {};

  for (let i = 0; i < subRoutes.length; i++) {
    const subRoute = subRoutes[i];

    if (subRoute.active.test(location.pathname)) {
      noResults.title = subRoute.noResultsTitle;
      noResults.msg = subRoute.noResultsMsg;
      noResults.img = subRoute.noResultsImg;
    }
  }

  const getActivePagetTitle = () => {
    const activePage = location.pathname;

    const activeRoute = subRoutes.find(function (routeItem) {
      const regex = routeItem.active;
      const active = regex.test(activePage);

      return active ? routeItem : false;
    });

    return activeRoute && activeRoute.page;
  };

  const handleViewSwitcher = () => {
    const viewTypeValue = viewType === 'list' ? 'grid' : 'list';

    setViewType(viewTypeValue);
    setLocalItem('favorites-view', viewTypeValue);
    mixpanelTrackInterestsDisplayModeChanged(viewTypeValue);
  };

  const loadMore = (page: string) => {
    if (page === 'viewed_me') {
      if (isMobile) {
        const storageHashIds = JSON.parse(getLocalItem(SliderSourceLSKey.INTERESTS_LS)) || [];

        if (storageHashIds.length > 0) {
          dispatch(markAsReadInterested(storageHashIds, false));
        } else {
          dispatch(refreshInterestedV2(false));
        }
      } else {
        dispatch(refreshInterested(false));
      }
    } else {
      dispatch(refreshFavs(page, false));
    }
  };

  const renderMoreExists = () => {
    if (!moreExists) return;
    return (
      <>
        {favList.length && !isFetching ? (
          <ButtonContainer>
            <Button
              buttonType="loadMore"
              onClick={() => {
                loadMore(page);
              }}
              // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
              customStyle={loadMoreButtonStyle}
            >
              Load More...
            </Button>
          </ButtonContainer>
        ) : (
          <ButtonContainer>
            <Loading />
          </ButtonContainer>
        )}
      </>
    );
  };

  return (
    <Container fullWidth screenTop>
      <Content className="favorites-content">
        <Aside className="favorites--menu-wrap">
          <TopNav
            className="favorites-sub-nav"
            subRoutes={subRoutes}
            favCount={userProfile.new_favorites}
            viewCount={userProfile.new_profile_views}
            themeDarkMode={isDarkMode()}
          />
        </Aside>
        <PrimaryContent>
          <FavoritesList>
            <SubHeader>
              <SubHeaderText data-test-id="favorites-sub-header">{getActivePagetTitle()}</SubHeaderText>
              <ViewActions
                data-test-id="list-grid-view-btn"
                onClick={() => handleViewSwitcher()}
                aria-label={viewType === 'list' ? 'List' : 'Grid'}
              >
                {viewType === 'list' ? <GridView /> : <ListView />}
              </ViewActions>
            </SubHeader>
            <FavoritedListWrapper data-test-id="fav-list-wrapper" isListView={viewType === 'list'}>
              <FavoritedList
                page={page}
                noResultsTitle={noResults.title}
                noResultsMsg={noResults.msg}
                noResultsImg={noResults.img}
                viewType={viewType}
                pageTitle={getActivePagetTitle()}
                themeDarkMode={isDarkMode()}
              />
            </FavoritedListWrapper>
            {renderMoreExists()}
          </FavoritesList>
        </PrimaryContent>
      </Content>
    </Container>
  );
};

export default memo(Interests);
